<script>
export default {
  name: 'OrderSummaryWidget',
  props: {
    email: {
      type: String,
      default: '',
    },
    agentName: {
      type: String,
      default: 'Support',
    },
  },
  emits: ['close', 'insertText'],
  data() {
    return {
      isLoading: true,
      error: null,
      orders: [],
      selectedOrderIndex: 0,
      comments: [],
    };
  },
  computed: {
    selectedOrder() {
      if (this.orders.length > 0) {
        return this.orders[this.selectedOrderIndex];
      }
      return null;
    },
  },
  watch: {
    email(newEmail) {
      if (newEmail) {
        this.fetchOrderData();
      }
    },
  },
  mounted() {
    if (this.email) {
      this.fetchOrderData();
    } else {
      this.isLoading = false;
      this.error = "No contact email provided.";
    }
  },
  methods: {
    async fetchOrderData() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await fetch('https://csalert.saevah.site/api/get_order_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.email }),
        });
        const data = await response.json();
        
        if (data.error) {
          this.error = "No order found for this email.";
        } else {
          this.orders = data.orders || [];
          this.selectedOrderIndex = 0;
          this.comments = data.comments || [];
        }
      } catch (err) {
        this.error = "Failed to communicate with order API.";
      } finally {
        this.isLoading = false;
      }
    },
    async onOrderChange() {
      if (!this.selectedOrder) return;
      this.comments = [];
      try {
        const response = await fetch('https://csalert.saevah.site/api/get_order_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email: this.email,
            orderId: this.selectedOrder.orderId
          }),
        });
        const data = await response.json();
        if (!data.error) {
          this.comments = data.comments || [];
        }
      } catch (err) {
        // ignore
      }
    },
    generateTemplate(actionType) {
      if (!this.selectedOrder) return;
      
      const customerName = this.selectedOrder.name || this.email.split('@')[0];
      const storeName = "Sammons Preston";
      const price = this.selectedOrder.orderAmount;
      const orderId = this.selectedOrder.orderId;
      const shipedVia = this.selectedOrder.shipedVia || 'Standard Shipping';
      
      let body = "";

      if (actionType === 'Refund') {
        body = `Hi ${customerName},\n\nThis is ${this.agentName} from ${storeName} support. We have processed a refund of $${price} for your Order #${orderId}. It may take 3-5 business days to reflect on your statement.\n\nBest Regards,\n${this.agentName}`;
      } else if (actionType === 'Cancel') {
        body = `Hi ${customerName},\n\nThis is ${this.agentName} from ${storeName}. As requested, your Order #${orderId} has been successfully canceled.\n\nBest Regards,\n${this.agentName}`;
      } else if (actionType === 'ETA') {
        body = `Hi ${customerName},\n\nThis is ${this.agentName} from ${storeName}. Your Order #${orderId} is currently being processed. It is being shipped via ${shipedVia}.\n\nBest Regards,\n${this.agentName}`;
      } else if (actionType === 'Backorder') {
        body = `Hi ${customerName},\n\nThis is ${this.agentName} from ${storeName}. Unfortunately, an item in your Order #${orderId} is currently on backorder. We apologize for the delay and will ship it as soon as possible.\n\nBest Regards,\n${this.agentName}`;
      }

      // Emit to parent to insert into ReplyBox
      this.$emit('insertText', body);
      this.$emit('close');
    }
  }
};
</script>

<template>
  <div class="order-summary-widget">
    <div class="widget-header">
      <h3 class="text-sm font-medium">Order Summary</h3>
      <button class="close-btn" @click="$emit('close')">
        <fluent-icon icon="dismiss" size="16" />
      </button>
    </div>
    
    <div class="widget-body">
      <div v-if="isLoading" class="p-4 text-center text-slate-500">
        Loading orders...
      </div>
      
      <div v-else-if="error" class="p-4 text-center text-red-500">
        {{ error }}
      </div>
      
      <div v-else-if="orders.length === 0" class="p-4 text-center text-slate-500">
        No orders found.
      </div>
      
      <div v-else class="order-content">
        <select v-model="selectedOrderIndex" @change="onOrderChange" class="order-select">
          <option v-for="(o, index) in orders" :key="index" :value="index">
            Order #{{ o.orderId }} - ${{ o.orderAmount }} ({{ o.orderDate }})
          </option>
        </select>
        
        <div class="order-details" v-if="selectedOrder">
          <p><strong>Name:</strong> {{ selectedOrder.name }}</p>
          <p><strong>Status:</strong> {{ selectedOrder.orderStatus }}</p>
          <p><strong>Payment:</strong> {{ selectedOrder.paymentType }}</p>
          <p><strong>Tracking:</strong> {{ selectedOrder.trackingNo || 'N/A' }}</p>
        </div>
        
        <div class="comments-section" v-if="comments.length > 0">
          <h4 class="text-xs font-semibold mb-2 text-slate-600">Order Comments</h4>
          <div class="comments-list">
            <div v-for="(comment, i) in comments" :key="i" class="comment-item">
              <span class="comment-date">{{ comment.dateEntered }}</span>
              <span class="comment-text">{{ comment.comment }}</span>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button class="action-btn refund-btn" @click="generateTemplate('Refund')">Refund</button>
          <button class="action-btn cancel-btn" @click="generateTemplate('Cancel')">Cancel</button>
          <button class="action-btn eta-btn" @click="generateTemplate('ETA')">ETA</button>
          <button class="action-btn backorder-btn" @click="generateTemplate('Backorder')">Backorder</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.order-summary-widget {
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 350px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--color-border-light);
  z-index: 1000;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  max-height: 500px;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-background-light);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-slate-500);
}

.close-btn:hover {
  color: var(--color-slate-700);
}

.widget-body {
  padding: 16px;
  overflow-y: auto;
}

.order-select {
  width: 100%;
  padding: 8px;
  margin-bottom: 12px;
  border-radius: 4px;
  border: 1px solid var(--color-border-light);
  font-size: 13px;
}

.order-details {
  background: var(--color-background-light);
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 12px;
}

.order-details p {
  margin: 4px 0;
}

.comments-section {
  margin-bottom: 16px;
}

.comments-list {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid var(--color-border-light);
  border-radius: 4px;
  padding: 8px;
  background: #fafafa;
}

.comment-item {
  font-size: 12px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.comment-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.comment-date {
  font-weight: 600;
  color: var(--color-slate-600);
  display: block;
  margin-bottom: 2px;
}

.action-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.action-btn {
  padding: 8px;
  border-radius: 4px;
  border: none;
  color: white;
  font-weight: 500;
  cursor: pointer;
  font-size: 13px;
}

.refund-btn { background: #10B981; }
.refund-btn:hover { background: #059669; }

.cancel-btn { background: #EF4444; }
.cancel-btn:hover { background: #DC2626; }

.eta-btn { background: #3B82F6; }
.eta-btn:hover { background: #2563EB; }

.backorder-btn { background: #F59E0B; }
.backorder-btn:hover { background: #D97706; }
</style>
